// src/pages/GamesArcade.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BBSHeader from '../components/BBS/BBSHeader';
import BBSNav from '../components/BBS/BBSNav';
import StatusBar from '../components/BBS/StatusBar';
import MeatMen from '../components/Games/MeatMen';
import VixiaPrime from '../components/Games/VixiaPrime';
import EscapeOphidiaGlobal from '../components/Games/EscapeOphidiaGlobal';
import EscapeOphidiaUnderground from '../components/Games/EscapeOphidiaUnderground';
import OPU1 from '../components/Games/OPU1';
import CheckTheBalance from '../components/Games/CheckTheBalance';
import ConspiracyDanLORD from '../components/Games/ConspiracyDanLORD';
import OPUWeb from '../components/Games/OPUWeb';


console.log('[ARCADE] CheckTheBalance imported as:', CheckTheBalance);


// ─── Static game component registry ───────────────────────────────────────────
const GAME_COMPONENTS = {
  MeatMen,
  VixiaPrime,
  EscapeOphidiaGlobal,
  EscapeOphidiaUnderground,
  OPU1,
  CheckTheBalance,
  ConspiracyDanLORD,
  OPUWeb,
};

// ─── Hidden games — session-unlocked only ─────────────────────────────────────
const HIDDEN_GAMES = {
  underbelly: {
    id: 'underbelly',
    component: 'EscapeOphidiaUnderground',
    name: 'ESCAPE OPHIDIA GLOBAL (DEV)',
    description: 'Escape the Ophidia Global Underground.',
    hidden: true,
    unlockedBy: 'underbelly',
  },
  opu1: {
    id: 'opu1',
    component: 'OPU1',
    name: 'O P U - 1',
    description: '[CLASSIFIED] The original organic processing unit. It has been thinking for 65 million years.',
    hidden: true,
    unlockedBy: 'opu1',
  },
  dan_lord: {
    id: 'dan_lord',
    component: 'ConspiracyDanLORD',
    name: 'THE SHED SKIN',
    description: '[DISTRESS SIGNAL] Conspiracy Dan is alive — trapped inside OPU-1103, serving drinks in a cyberpunk tavern. Talk to him. Fight through the undercity. Free what\'s left of him.',
    hidden: true,
    unlockedBy: 'dan_lord',
  },
  opuweb: {
    id: 'opuweb',
    component: 'OPUWeb',
    name: 'OPU-WEB: SWARM DEFENSE',
    description: '[CLASSIFIED] Barbara is waking up. The drone swarm is reactivating. Man the defense terminal before the web closes around you.',
    hidden: true,
    unlockedBy: 'opuweb',
  },
};

export default function GamesArcade() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [activeGame, setActiveGame] = useState(null);
  const [loading, setLoading] = useState(true);

  // Unlocked hidden games — session only
  const [unlockedKeys, setUnlockedKeys] = useState(() => {
    const keys = [];
    if (sessionStorage.getItem('unlock_underbelly') === 'true') keys.push('underbelly');
    if (sessionStorage.getItem('unlock_opu1') === 'true') keys.push('opu1');
    if (sessionStorage.getItem('unlock_dan_lord') === 'true') keys.push('dan_lord');
    if (sessionStorage.getItem('unlock_opuweb') === 'true') keys.push('opuweb');  
    return keys;
  });

  // ── Listen for unlock events ─────────────────────────────────────────────────
  useEffect(() => {
    function onUnlock(e) {
      const { key } = e.detail;
      setUnlockedKeys(prev => prev.includes(key) ? prev : [...prev, key]);
    }
    window.addEventListener('arcade-unlock', onUnlock);
    return () => window.removeEventListener('arcade-unlock', onUnlock);
  }, []);

  // ── Load API games list ──────────────────────────────────────────────────────
  useEffect(() => {
    fetch('/api/games')
      .then(res => {
        if (!res.ok) throw new Error('Failed to load games');
        return res.json();
      })
      .then(data => {
        setGames(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load games:', err);
        setGames([]);
        setLoading(false);
      });
  }, []);

  // ── Auto-launch from URL /games/:gameId ──────────────────────────────────────
  useEffect(() => {
    if (!gameId || activeGame) return;

    // Check hidden games first
    const hiddenGame = Object.values(HIDDEN_GAMES).find(g => g.component === gameId);
    if (hiddenGame && unlockedKeys.includes(hiddenGame.unlockedBy)) {
      setActiveGame(hiddenGame);
      return;
    }

    // Then API games
    if (games.length > 0) {
      const found = games.find(g => g.component === gameId);
      if (found) setActiveGame(found);
    }
  }, [gameId, games, unlockedKeys, activeGame]);

  // ── Called by EscapeOphidiaUnderground on victory ────────────────────────────
  const handleUnderbellyEscape = useCallback(() => {
    // Unlock OPU-1
    sessionStorage.setItem('unlock_opu1', 'true');
    sessionStorage.setItem('opu1_unlocked', 'true');
    setUnlockedKeys(prev => prev.includes('opu1') ? prev : [...prev, 'opu1']);
    window.dispatchEvent(new CustomEvent('arcade-unlock', { detail: { key: 'opu1' } }));

    // Unlock The Shed Skin (Dan LORD)
    sessionStorage.setItem('unlock_dan_lord', 'true');
    sessionStorage.setItem('dan_lord_unlocked', 'true');
    setUnlockedKeys(prev => prev.includes('dan_lord') ? prev : [...prev, 'dan_lord']);
    window.dispatchEvent(new CustomEvent('arcade-unlock', { detail: { key: 'dan_lord' } }));
  }, []);

  function launchGame(game) {
    setActiveGame(game);
    navigate(`/games/${game.component}`);
  }

  function exitGame() {
    setActiveGame(null);
    navigate('/games');
  }

  // ── Render active game ───────────────────────────────────────────────────────
  if (activeGame) {
    const GameComponent = GAME_COMPONENTS[activeGame.component];
    if (!GameComponent) {
      return (
        <div>
          <BBSHeader title="ERROR" />
          <BBSNav />
          <div style={{ color: '#ff0040', textAlign: 'center', padding: '40px' }}>
            GAME MODULE NOT FOUND — SIGNAL CORRUPTED
          </div>
          <button className="bbs-button" onClick={exitGame}>EXIT</button>
          <StatusBar />
        </div>
      );
    }

    // Determine which props to pass based on game
    const gameProps = {};
    if (activeGame.component === 'EscapeOphidiaUnderground') {
      gameProps.onEscape = handleUnderbellyEscape;
    }

    return (
      <div>
        <BBSHeader title={activeGame.name} />
        <button className="bbs-back" onClick={exitGame}>
          {'<'} EXIT GAME
        </button>
        <hr className="bbs-divider" />
        <GameComponent {...gameProps} />
        <StatusBar />
      </div>
    );
  }

  // ── Merge API games + unlocked hidden games ──────────────────────────────────
  const visibleHidden = unlockedKeys
    .map(key => HIDDEN_GAMES[key])
    .filter(Boolean);

  const allGames = [...games, ...visibleHidden];

  // ── Game card styling helper ─────────────────────────────────────────────────
  function getCardStyle(game) {
    if (!game.hidden) return {};

    if (game.component === 'OPU1') {
      return {
        borderColor: '#330055',
        background: 'rgba(5, 0, 12, 0.6)',
      };
    }
    if (game.component === 'ConspiracyDanLORD') {
      return {
        borderColor: '#441111',
        background: 'rgba(12, 2, 2, 0.6)',
      };
    }
    if (game.component === 'OPUWeb') {
      return { borderColor: '#663300', background: 'rgba(12, 6, 0, 0.6)' };
    }
    // Underbelly
    return {
      borderColor: '#3a2e1e',
      background: 'rgba(8, 5, 2, 0.6)',
    };
  }

  function getLabelColor(game) {
    if (!game.hidden) return '#00ff41';
    if (game.component === 'OPU1') return '#8b00ff';
    if (game.component === 'ConspiracyDanLORD') return '#ff0040';
    if (game.component === 'OPUWeb') return '#ff6600';
    return '#9b7a4a';
  }

  function getBadge(game) {
    if (!game.hidden) return null;
    if (game.component === 'OPU1') {
      return { text: '▓ CLASSIFIED ▓', color: '#6622aa' };
    }
    if (game.component === 'ConspiracyDanLORD') {
      return { text: '▓ DISTRESS SIGNAL ▓', color: '#882222' };
    }
    if (game.component === 'OPUWeb') {
      return { text: '▓ THREAT ACTIVE ▓', color: '#884400' };
    }
    return { text: '▓ UNLOCKED ▓', color: '#55441e' };
  }

  function getDescColor(game) {
    if (!game.hidden) return undefined;
    if (game.component === 'OPU1') return '#aa77cc';
    if (game.component === 'ConspiracyDanLORD') return '#cc8888';
    if (game.component === 'OPUWeb') return '#cc8844';
    return '#9b7a4a';
  }

  // ── Render arcade list ───────────────────────────────────────────────────────
  return (
    <div>
      <BBSHeader title="GAMES ARCADE" subtitle="INTERACTIVE TERMINALS" />
      <BBSNav />

      <div style={{ color: '#ff0040', textAlign: 'center', marginBottom: '20px', fontSize: '0.9rem' }}>
        ▓ WARNING: GAMES CONTAIN DEPICTIONS OF EXTREME VIOLENCE ▓
      </div>

      {loading ? (
        <div className="bbs-loading">
          LOADING ARCADE<span className="bbs-loading-dots" />
        </div>
      ) : allGames.length === 0 ? (
        <div style={{ color: '#555', textAlign: 'center', padding: '40px' }}>
          NO GAMES AVAILABLE — CHECK BACK LATER
        </div>
      ) : (
        <div>
          {allGames.map((game, i) => {
            const badge = getBadge(game);

            return (
              <div
                key={game.id}
                className="bbs-story-card"
                onClick={() => launchGame(game)}
                style={{
                  cursor: 'pointer',
                  ...getCardStyle(game),
                }}
              >
                <div className="story-title">
                  <span style={{
                    color: getLabelColor(game),
                    marginRight: '10px',
                  }}>
                    {game.hidden ? '[??]' : `[${String(i + 1).padStart(2, '0')}]`}
                  </span>
                  {game.name}
                  {badge && (
                    <span style={{
                      marginLeft: '12px',
                      fontSize: '0.75rem',
                      color: badge.color,
                      letterSpacing: '2px',
                    }}>
                      {badge.text}
                    </span>
                  )}
                </div>
                <div
                  className="story-excerpt"
                  style={{
                    marginTop: '5px',
                    color: getDescColor(game),
                  }}
                >
                  {game.description}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <StatusBar />
    </div>
  );
}